import React, { useMemo, useState } from "react";
import { apiFetch } from "../../utils/api";
import socket from "../../socket";
import { useSettingsRegistry } from "./SettingsContext";

export default function SyncSettings({ stationId }) {
  const registry = useSettingsRegistry();
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canSync = useMemo(() => !!stationId, [stationId]);

  const buildPrompt = (targetCount, changes) => {
    const header = `Send saved settings to ${targetCount} station${targetCount === 1 ? "" : "s"} in this sublocation?`;
    if (!changes.length) return header;
    return (
      `${header}\n\nUnsaved changes (will NOT be sent):\n` +
      changes.map((c) => `- ${c}`).join("\n")
    );
  };

  const handleSync = async () => {
    if (!stationId) return;

    setLastResult(null);
    setSyncing(true);
    registry.setLoadingMessage("Syncing settings...");
    registry.setLoading(true);

    try {
      const stationRes = await apiFetch(
        `/updates-reports/stations/by-station-id/${stationId}`
      );
      if (!stationRes.ok) {
        throw new Error("Failed to load current station details.");
      }
      const station = await stationRes.json();
      const sublocationId = station?.sublocation_id;
      if (!sublocationId) {
        throw new Error("Current station has no sublocation assigned.");
      }

      const listRes = await apiFetch(
        `/updates-reports/stations?sublocation_id=${encodeURIComponent(sublocationId)}`
      );
      if (!listRes.ok) {
        throw new Error("Failed to load stations for sublocation.");
      }
      const stations = await listRes.json();
      const targetStationIds = (stations || [])
        .map((s) => s.station_id)
        .filter((id) => String(id) !== String(stationId));

      if (!targetStationIds.length) {
        setLastResult({
          ok: false,
          message: "No other stations found in this sublocation.",
        });
        return;
      }

      const changes = registry.getAllChanges();
      const ok = window.confirm(buildPrompt(targetStationIds.length, changes));
      if (!ok) return;

      await registry.applySavedToStations(targetStationIds);
      socket.emit("settings-sync-refresh", {
        sender_station_id: Number(stationId),
        sublocation_id: Number(sublocationId),
        mirrored_station_ids: targetStationIds.map((id) => Number(id)).filter(Number.isFinite),
      });
      setLastResult({
        ok: true,
        message: `Synced to ${targetStationIds.length} station${targetStationIds.length === 1 ? "" : "s"}.`,
      });
    } catch (err) {
      console.error("Failed to sync settings:", err);
      setLastResult({
        ok: false,
        message: err?.message || "Failed to sync settings.",
      });
    } finally {
      registry.setLoading(false);
      registry.setLoadingMessage("Saving settings...");
      setSyncing(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl">
      <h3 className="mb-2 text-center text-lg font-semibold text-gray-800 dark:text-gray-200">
        Sync Settings
      </h3>
      <p className="mb-4 text-center text-sm text-gray-600 dark:text-gray-400">
        Send the last saved settings from this station to all other screens in the
        same sublocation.
      </p>

      {!canSync && (
        <div className="mb-4 text-center text-sm text-amber-600 dark:text-amber-300">
          Sync is only available when settings are opened from a specific station.
        </div>
      )}

      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleSync}
          disabled={!canSync || syncing}
          className={`min-w-[180px] rounded-xl px-6 py-3 text-base font-semibold ${
            syncing || !canSync
              ? "cursor-not-allowed bg-gray-400 text-white"
              : "bg-indigo-600 text-white hover:bg-indigo-700"
          }`}
        >
          {syncing ? "Syncing..." : "Send to all"}
        </button>
      </div>

      {lastResult && (
        <div
          className={`mt-3 text-center text-sm ${
            lastResult.ok
              ? "text-green-600 dark:text-green-400"
              : "text-red-600 dark:text-red-400"
          }`}
        >
          {lastResult.message}
        </div>
      )}
    </div>
  );
}
